import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Users, FileText, CheckCircle, XCircle, Loader2, 
  Eye, Download, Briefcase, MapPin, Phone, Mail,
  Clock, Award, ChevronDown, ChevronUp, AlertTriangle,
  UserPlus, Trash2, ExternalLink, Calendar, RefreshCw
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'caregiver', label: 'Caregiver (CNA)' },
  { value: 'dsp', label: 'DSP' },
  { value: 'lpn', label: 'LPN' },
  { value: 'rn', label: 'RN' },
];

// Calendly URL - Integrity Home Care Wilmington
const CALENDLY_URL = 'https://calendly.com/integrityhomecarewilmington';

export default function ApplicantReview() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedApp, setExpandedApp] = useState(null);
  const [documents, setDocuments] = useState({});
  const [loadingDocs, setLoadingDocs] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [selectedRole, setSelectedRole] = useState({});
  const [showRejectConfirm, setShowRejectConfirm] = useState(null);

  useEffect(() => {
    fetchPendingApplications();
  }, []);

  const fetchPendingApplications = async () => {
    try {
      const response = await api.get('/hiring/pending');
      setApplications(response.data);
      
      // Initialize selected roles based on position_type
      const roles = {};
      response.data.forEach(app => {
        roles[app.id] = app.position_type === 'cna' ? 'caregiver' : app.position_type;
      });
      setSelectedRole(roles);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load pending applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (applicationId, folderPath) => {
    if (documents[applicationId]) return; // Already loaded
    
    setLoadingDocs(prev => ({ ...prev, [applicationId]: true }));
    
    try {
      const response = await api.get(`/hiring/documents/${encodeURIComponent(folderPath)}`);
      setDocuments(prev => ({ ...prev, [applicationId]: response.data.documents }));
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleExpand = async (application) => {
    if (expandedApp === application.id) {
      setExpandedApp(null);
    } else {
      setExpandedApp(application.id);
      if (application.folder_path) {
        await fetchDocuments(application.id, application.folder_path);
      }
    }
  };

  const handleRegeneratePDF = async (application) => {
    setRegeneratingId(application.id);
    
    try {
      const response = await api.post(`/applications/${application.id}/regenerate-pdf`);
      
      toast.success(
        <div>
          <p className="font-semibold">PDF Regenerated!</p>
          <p className="text-sm text-gray-600">Application PDF has been updated with correct formatting.</p>
        </div>
      );
      
      // Refresh documents for this application
      if (application.folder_path) {
        setDocuments(prev => ({ ...prev, [application.id]: null }));
        await fetchDocuments(application.id, application.folder_path);
      }
      
      // Refresh the applications list to get updated PDF URL
      await fetchPendingApplications();
    } catch (error) {
      console.error('Failed to regenerate PDF:', error);
      toast.error('Failed to regenerate PDF: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleHire = async (application) => {
    setProcessingId(application.id);
    
    try {
      const role = selectedRole[application.id] || 'caregiver';
      const response = await api.post(`/hiring/hire/${application.id}`, { role });
      
      toast.success(
        <div>
          <p className="font-semibold">{application.first_name} {application.last_name} has been hired!</p>
          <p className="text-sm text-gray-600">Welcome email sent to {application.email}</p>
        </div>
      );
      
      // Remove from list
      setApplications(prev => prev.filter(app => app.id !== application.id));
      setExpandedApp(null);
    } catch (error) {
      console.error('Failed to hire applicant:', error);
      toast.error(error.response?.data?.detail || 'Failed to hire applicant');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (application, deleteRecord = false) => {
    setProcessingId(application.id);
    setShowRejectConfirm(null);
    
    try {
      await api.post(`/hiring/reject/${application.id}?delete_documents=true&delete_record=${deleteRecord}`);
      
      toast.success(
        <div>
          <p className="font-semibold">Application rejected</p>
          <p className="text-sm text-gray-600">Documents cleaned up & notification sent</p>
        </div>
      );
      
      // Remove from list
      setApplications(prev => prev.filter(app => app.id !== application.id));
      setExpandedApp(null);
    } catch (error) {
      console.error('Failed to reject applicant:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject applicant');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendCalendlyInvite = (application) => {
    // Open Calendly link with pre-filled email
    const calendlyWithEmail = `${CALENDLY_URL}?email=${encodeURIComponent(application.email)}&name=${encodeURIComponent(application.first_name + ' ' + application.last_name)}`;
    window.open(calendlyWithEmail, '_blank');
    toast.success(`Interview scheduling link opened for ${application.first_name}`);
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
      case 'image': return <Eye className="w-4 h-4 text-blue-500" />;
      default: return <Download className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Users className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Pending Applications
            </h2>
            <p className="text-gray-600">Review and process new hire applications</p>
          </div>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-amber-700 font-medium">
            <Clock className="w-4 h-4" />
            {applications.length} pending
          </span>
        </div>
      </div>

      {/* Applications List */}
      {applications.length === 0 ? (
        <Card className="p-12 text-center border-purple-100">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
          <p className="text-gray-600">No pending applications to review</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((application) => (
            <Card 
              key={application.id} 
              className={`border-2 transition-all ${
                expandedApp === application.id 
                  ? 'border-purple-300 shadow-lg' 
                  : 'border-purple-100 hover:border-purple-200'
              }`}
            >
              {/* Application Header */}
              <div 
                className="p-6 cursor-pointer"
                onClick={() => handleExpand(application)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-full">
                      <Briefcase className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {application.first_name} {application.last_name}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="inline-flex items-center gap-1">
                          <Award className="w-4 h-4" />
                          {application.position_type.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {application.city}, {application.state}
                        </span>
                        {application.years_experience && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {application.years_experience} yrs exp
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">
                      Applied {formatDate(application.created_at)}
                    </span>
                    {expandedApp === application.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedApp === application.id && (
                <div className="border-t border-purple-100 p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-purple-600" />
                        Contact Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-500">Email:</span> <a href={`mailto:${application.email}`} className="text-purple-600 hover:underline">{application.email}</a></p>
                        <p><span className="text-gray-500">Phone:</span> <a href={`tel:${application.phone}`} className="text-purple-600 hover:underline">{application.phone}</a></p>
                        {application.license_number && (
                          <p><span className="text-gray-500">License:</span> {application.license_number}</p>
                        )}
                      </div>
                    </div>

                    {/* Documents */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-600" />
                        Application Documents
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRegeneratePDF(application)}
                          disabled={regeneratingId === application.id}
                          className="ml-auto text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                          title="Regenerate PDF with correct formatting"
                          data-testid={`regenerate-pdf-${application.id}`}
                        >
                          {regeneratingId === application.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          <span className="ml-1 text-xs">Regenerate PDF</span>
                        </Button>
                      </h4>
                      
                      {loadingDocs[application.id] ? (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading documents...
                        </div>
                      ) : documents[application.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {documents[application.id].map((doc, idx) => (
                            <a
                              key={idx}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                              data-testid={`doc-${application.id}-${idx}`}
                            >
                              {getDocumentIcon(doc.type)}
                              <span className="flex-1 text-sm text-gray-700 truncate">{doc.name}</span>
                              <ExternalLink className="w-4 h-4 text-gray-400" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No documents found</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      {/* Role Selection */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700">Assign Role:</label>
                        <select
                          value={selectedRole[application.id] || 'caregiver'}
                          onChange={(e) => setSelectedRole(prev => ({ ...prev, [application.id]: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          data-testid={`role-select-${application.id}`}
                        >
                          {ROLE_OPTIONS.map(role => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Decision Buttons */}
                      <div className="flex items-center gap-3">
                        {showRejectConfirm === application.id ? (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            <span className="text-sm text-red-700">Delete documents & notify?</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowRejectConfirm(null)}
                              className="border-gray-300"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleReject(application, false)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              disabled={processingId === application.id}
                            >
                              {processingId === application.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Confirm'
                              )}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => handleSendCalendlyInvite(application)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              data-testid={`calendly-btn-${application.id}`}
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Schedule Interview
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setShowRejectConfirm(application.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              disabled={processingId === application.id}
                              data-testid={`reject-btn-${application.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Don't Hire
                            </Button>
                            <Button
                              onClick={() => handleHire(application)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              disabled={processingId === application.id}
                              data-testid={`hire-btn-${application.id}`}
                            >
                              {processingId === application.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <UserPlus className="w-4 h-4 mr-2" />
                              )}
                              Hire
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
